import {
  api_base_websocket_url,
  build_default_websocket_url,
  normalize_websocket_path,
  websocket_url_from_base,
} from "./one_chat_websocket_helpers";
import {
  attach_socket_handlers,
  calculate_reconnect_delay,
  close_socket,
  create_socket,
  is_socket_connecting_or_open,
} from "./one_chat_websocket_connection";

const DEFAULT_DRIVE_WS_PATH = "/ws/drive/";

function drive_websocket_path() {
  return process.env.VUE_APP_DRIVE_WS_PATH || DEFAULT_DRIVE_WS_PATH;
}

function configured_drive_websocket_base_url() {
  return process.env.VUE_APP_DRIVE_WS_URL || process.env.VUE_APP_WEBSOCKET_BASE_URL || "";
}

function preferred_drive_websocket_base_url(path) {
  const configured_url = configured_drive_websocket_base_url();
  if (configured_url) return websocket_url_from_base(configured_url, path);
  return api_base_websocket_url(path) || build_default_websocket_url(path);
}

function assert_drive_websocket_protocol(url) {
  if (["ws:", "wss:"].includes(url.protocol)) return;
  throw new Error("Invalid Drive WebSocket protocol.");
}

export function build_drive_websocket_url(options = {}) {
  const path = normalize_websocket_path(options.path || DEFAULT_DRIVE_WS_PATH);
  const url = new URL(preferred_drive_websocket_base_url(path));
  assert_drive_websocket_protocol(url);
  if (options.token) url.searchParams.set("token", options.token);
  return url.toString();
}

function build_default_options(options = {}) {
  return ({
    path: drive_websocket_path(),
    token: null,
    reconnect_min_delay: 2000,
    reconnect_max_delay: 8000,
    reconnect_jitter: 500,
    heartbeat_interval: 30000,
    ...options,
  });
}

function invoke_drive_websocket_callback(options, name, payload) {
  const callback = Reflect.get(options, name);
  if (typeof callback === "function") callback(payload);
}

function parse_drive_socket_payload(event) {
  try { // NOPMD - required to handle invalid WebSocket JSON safely
    return JSON.parse(event.data);
  } catch (error) {
    console.warn("Ignored invalid Drive WebSocket payload.", error);
    return null;
  }
}

export default class DriveWebSocketClient {
  constructor(options = {}) {
    this.options = build_default_options(options);
    this.socket = null;
    this.closed_by_client = false;
    this.reconnect_attempts = 0;
    this.reconnect_timer = null;
    this.heartbeat_timer = null;
  }

  connect() {
    if (is_socket_connecting_or_open(this.socket)) return;

    this.closed_by_client = false;
    this.clear_reconnect_timer();
    this.socket = create_socket(build_drive_websocket_url(this.options));
    attach_socket_handlers(this, this.socket);
  }

  disconnect() {
    this.closed_by_client = true;
    this.clear_reconnect_timer();
    this.clear_heartbeat();
    close_socket(this.socket);
    this.socket = null;
  }

  handle_open(event) {
    this.reconnect_attempts = 0;
    this.start_heartbeat();
    this.invoke_callback("on_open", event);
  }

  handle_close(event) {
    this.clear_heartbeat();
    this.invoke_callback("on_close", event);
    if (!this.closed_by_client) this.schedule_reconnect();
  }

  handle_message(event) {
    const payload = parse_drive_socket_payload(event);
    if (!payload) return;
    this.invoke_callback("on_event", payload);
  }

  invoke_callback(name, payload) {
    invoke_drive_websocket_callback(this.options, name, payload);
  }

  schedule_reconnect() {
    this.reconnect_attempts += 1;
    const delay = calculate_reconnect_delay(this.options, this.reconnect_attempts);
    this.reconnect_timer = window.setTimeout(() => this.connect(), delay);
  }

  clear_reconnect_timer() {
    if (!this.reconnect_timer) return;
    window.clearTimeout(this.reconnect_timer);
    this.reconnect_timer = null;
  }

  start_heartbeat() {
    this.clear_heartbeat();
    this.heartbeat_timer = window.setInterval(
      () => this.send({ type: "drive.heartbeat" }),
      this.options.heartbeat_interval
    );
  }

  clear_heartbeat() {
    if (!this.heartbeat_timer) return;
    window.clearInterval(this.heartbeat_timer);
    this.heartbeat_timer = null;
  }

  is_open() {
    return Boolean(this.socket && this.socket.readyState === WebSocket.OPEN);
  }

  send(payload) {
    if (!this.is_open()) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }
}
